import { observer } from 'mobx-react';
import {
    Enums,
    ModalHandle,
    SoftwareUpdateAlert,
} from 'Mri.Web.Common';
import * as React from 'react';
import { ActiveDisplay } from '../../ActiveDisplay';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { useStore } from '../../../hooks/use-store';
import { ContractModal } from '../../../Contracts/ContractModal';
import { TicketModal } from '../../TicketModal';
import { TicketsModal } from '../../TicketsModal';
import { DisplayEventsModal } from '../../DisplayEventsModal';
import { DisplayGridSnapshot } from '../DisplayGridSnapshot';
import {SerialNumberFieldContents} from "./SerialNumberFieldContents";

export interface IDisplayFieldProps extends ICellRendererParams<ActiveDisplay> {
    value: ActiveDisplay;
}


export const SelectAllHeader = observer((props: any) => {
    const { appStore } = useStore();
    const [isChecked, setIsChecked] = React.useState(false);

    function handleCheckboxChange(e: React.ChangeEvent<HTMLInputElement>) {
        const checked = e.target.checked;
        setIsChecked(checked);
        appStore.gridRef?.api.forEachNode(node => node.setSelected(checked));
    }
    const wrapperClass = "ag-wrapper ag-input-wrapper ag-checkbox-input-wrapper";
    return (
        <div className={isChecked ? wrapperClass + " ag-checked" : wrapperClass} style={{marginLeft:"10px"}}>
            <input
                type="checkbox"
                checked={isChecked}
                onChange={handleCheckboxChange}
                className="ag-input-field-input ag-checkbox-input"
            />
        </div>
    );
});

export function RowNumberField(props: IDisplayFieldProps) {
    if(props.node.rowIndex != null)
        return (
            <div>{props.node.rowIndex + 1}</div>
        );
}

export function SerialNumberField(props: IDisplayFieldProps) {
    const display = props.data;
    
    if (display) {
        return (<SerialNumberFieldContents {...props}/>);
    }
    return <div>-</div>

}

export function AddressField(props: IDisplayFieldProps) {
    const display = props.data;
    return (
        <div>
            {display && display.fullAddress
                    ? display.fullAddress +
                    (display?.measurements?.find(s => s.id == Mri.WebApi.MeasurementId.GpsLatitude)?.floatValue
                        ? ' (GPS)'
                        : display?.configPoll?.geoVuConfig?.latitude
                            ? ' (Config)'
                            : ' (SmartVu)')
                    : '-'}
            </div>
        );
}

export function SoftwareUpdateStatusField(props: IDisplayFieldProps) {
    const display = props.data;
    return (
        <div>
            {display &&
                <SoftwareUpdateAlert updateStatus={display.displaySoftwareUpdateStatus} />
            }

            <span className="px-2 pt-2">
                {display && display.displaySoftwareUpdateStatus && display.displaySoftwareUpdateStatus.state
                    ? Enums.toString(Enums.SoftwareUpdateState, display.displaySoftwareUpdateStatus.state)
                    : '-'}
            </span>
        </div>
    );
}

export const ContractField = observer((props: IDisplayFieldProps) => {
    const display = props.data;
    const { contractsStore } = useStore();
    const contract = contractsStore.contracts.get(display?.contract || '');
    const contractModalHandle = new ModalHandle();
    function handleClick(e: React.MouseEvent) {
        e.preventDefault();
        contractModalHandle.open();
    }

    return (
        <div>
            <a className="btn-link" onClick={handleClick}>
                {contract?.contractNumber
                    ? contract?.contractNumber
                    : contract?.serviceCO
                        ? contract?.serviceCO
                        : '-'}
            </a>

            {contract && (
                <ContractModal
                    contract={contract}
                    display={display}
                    modalHandle={contractModalHandle}
                    viewState="readOnly"
                />
            )}
        </div>
    );
})

export const TicketsCountField = observer((props: IDisplayFieldProps) => {
    const display = props.data;
    const createTicketModalHandle = new ModalHandle();
    const ticketsModalHandle = new ModalHandle();

    function handleClick(e: React.MouseEvent) {
        e.preventDefault();
        if(display) {
            !display.openTicketCount ? createTicketModalHandle.open() : ticketsModalHandle.open();
        }
    }

    return (
        <div>
            <a className="btn-link" onClick={handleClick}>
                {display ? display.openTicketCount : 0}
            </a>

            {display &&
                <>
                    <TicketModal display={display} modalHandle={createTicketModalHandle} />
                    <TicketsModal display={display} modalHandle={ticketsModalHandle} history={history} />
                </>
            }
        </div>
    );
})

export const EventsCountField = observer((props: IDisplayFieldProps) => {
    const display = props.data;
    const eventsModalHandle = new ModalHandle();
    const { displayFiltersStore } = useStore();
    let displayFilter = displayFiltersStore.storage.displayFilter;
    
    function handleClick(e: React.MouseEvent) {
        e.preventDefault();
        eventsModalHandle.open();
    }

    return (
        <div>
            <a className="btn-link" onClick={handleClick}>
                <span>{display ? display.newDisplayEventCount : 0}</span>
            </a>

            {display &&
                <DisplayEventsModal
                    display={display}
                    displayFilter={displayFilter}
                    modalHandle={eventsModalHandle}
                    history={history}
                    hideAlerts
                />
            }
        </div>
    );
})

export const AlertsCountField = observer((props: IDisplayFieldProps) => {
    const display = props.data;
    const alertsModalHandle = new ModalHandle();
    const { displayFiltersStore } = useStore();
    let displayFilter = displayFiltersStore.storage.displayFilter;

    function handleClick(e: React.MouseEvent) {
        e.preventDefault();
        alertsModalHandle.open();
    }

    return (
        <div>
            <a className="btn-link" onClick={handleClick}>
                <span>{display ? display.displayAlertCount : 0}</span>
            </a>

            {display && 
                <DisplayEventsModal
                    display={display}
                    displayFilter={displayFilter}
                    modalHandle={alertsModalHandle}
                    history={history}
                    showOnlyAlerts
                />
            }
        </div>
    );
})


export const PopVuSide1Field = observer((props: IDisplayFieldProps) => {
    const display = props.data;
    if(!display) return(<div>-</div>);
    return (    
        <DisplayGridSnapshot displaySerialNumber={display.serialNumber} screenSnapshotNumber={0} />
    );
})

export const PopVuSide2Field = observer((props: IDisplayFieldProps) => {
    const display = props.data;

    if(!display) return(<div>-</div>);
    return (
        <DisplayGridSnapshot displaySerialNumber={display.serialNumber} screenSnapshotNumber={1} />
    );
})

export const InfiniteTouch1Field = observer((props: IDisplayFieldProps) => {
    const display = props.data;
    if(!display) return(<div>-</div>);
    return (
        <DisplayGridSnapshot displaySerialNumber={display.serialNumber} infiniteTouch screenSnapshotNumber={0} />
    );
})

export const InfiniteTouch2Field = observer((props: IDisplayFieldProps) => {
    const display = props.data;
    if(!display) return(<div>-</div>);
    return (
        <DisplayGridSnapshot displaySerialNumber={display.serialNumber} infiniteTouch screenSnapshotNumber={1} />
    );
});

export interface ISortField {
    colId: string,
    sortString: string
}

export const sortFields: ISortField[] = [
    {
        colId: "serialNumber",
        sortString: "SerialNumber",
    },
    {
        colId: "unitSerialNumber",
        sortString: "Display.UnitSerialNumber",
    },
    {
        colId: "roleInUnit",
        sortString: "ActiveDisplayStatus.LastKnownPoll?.DisplayStatusPoll?.SystemInfo?.Value.roleInUnit",
    },
    {
        colId: "contractData.serviceLevel",
        sortString: "Contract?.ServiceLevel",
    },
    {
        colId: "contractData.advancedWarrantyAlerts",
        sortString: "Contract?.AdvancedWarrantyAlerts",
    },
    {
        colId: "unitLocation.customerSiteID",
        sortString: "UnitLocation?.CustomerSiteID",
    },
    {
        colId: "accountName",
        sortString: "Display.Account",
    },
    {
        colId: "openTicketCount",
        sortString: "OpenTicketCount",
    },
    {
        colId: "displayAlertCount",
        sortString: "DisplayAlertCount",
    },
    {
        colId: "newDisplayEventCount",
        sortString: "NewDisplayEventCount",
    },
    {
        colId: "statusMessage",
        sortString: "StatusRank",
    },
    {
        colId: "webApiKickDisabled",
        sortString: "Display.WebApiKickDisabled",
    },
    {
        colId: "unitLocation.address1",
        sortString: "UnitLocation?.Address1",
    },
    {
        colId: "unitLocation.city",
        sortString: "UnitLocation?.City",
    },
    {
        colId: "unitLocation.stateOrProvince",
        sortString: "UnitLocation?.StateOrProvince",
    },
    {
        colId: "unitLocation.country",
        sortString: "UnitLocation?.Country",
    },
    {
        colId: "uptime",
        sortString: "ActiveDisplayStatus.LastKnownPoll?.DisplayStatusPoll?.Uptime",
    },
    {
        colId: "shippedDate",
        sortString: "ShippedDate",
    },
    {
        colId: "contract",
        sortString: "Display.Contract",
    },
    {
        colId: "pollTime",
        sortString: "DateTime(ActiveDisplayStatus.LastKnownPoll?.PollTime)",
    },
    {
        colId: "displayConnectionData.displayBoardIP",
        sortString: "Display.DisplayConnectionData?.DisplayBoardIP",
    },
    {
        colId: "displaySoftwareUpdateStatus.state",
        sortString: "SoftwareUpdateStatusPoll?.State",
    },
    {
        colId: "partNumber",
        sortString: "Display.UnitPartNumber",
    },
    {
        colId: "modelNumber",
        sortString: "Display.ModelNumber",
    },
    {
        colId: "timezone",
        sortString: "ActiveDisplayStatus.LastKnownPoll?.DisplayStatusPoll?.Timezone",
    },
    {
        colId: "displaySoftware.mriSoftwareVersion",
        sortString: "DisplaySoftware?.MriSoftwareVersion",
    },
    {
        colId: "displaySoftware.bootLoaderSoftwareVersion",
        sortString: "DisplaySoftware?.BootLoaderSoftwareVersion",
    },
    {
        colId: "displaySoftware.bootSoftwareVersion",
        sortString: "DisplaySoftware?.BootSoftwareVersion",
    },
    {
        colId: "displaySoftware.kernelSoftwareVersion",
        sortString: "DisplaySoftware?.KernelSoftwareVersion",
    },
    {
        colId: "displaySoftware.rootSoftwareVersion",
        sortString: "DisplaySoftware?.RootSoftwareVersion",
    },
    {
        colId: "displaySoftware.infiniteTouchConfigVersionPrimary",
        sortString: "DisplaySoftware?.InfiniteTouchConfigVersionPrimary",
    },
    {
        colId: "displaySoftware.infiniteTouchFirmwareVersionPrimary",
        sortString: "DisplaySoftware?.InfiniteTouchFirmwareVersionPrimary",
    },
    {
        colId: "displaySoftware.infiniteTouchConfigVersionSecondary",
        sortString: "DisplaySoftware?.InfiniteTouchConfigVersionSecondary",
    },
    {
        colId: "displaySoftware.infiniteTouchFirmwareVersionSecondary",
        sortString: "DisplaySoftware?.InfiniteTouchFirmwareVersionSecondary",
    },
    {
        colId: "powerGovSourceName",
        sortString: "ActiveDisplayStatus.LastKnownPoll?.DisplayStatusPoll?.PowerGovSourceName",
    },
    {
        colId: "displayConnectionData.customerDisplayID",
        sortString: "Display.DisplayConnectionData?.CustomerDisplayID",
    },
    {
        colId: "displayConnectionData.nocServer",
        sortString: "Display.DisplayConnectionData?.NocServer",
    },
    {
        colId: "statusMessage",
        sortString: "StatusRank",
    },
]